import type React from 'react';

/**
 * To make clear which attributes belong to a disclosure pattern.
 *
 * https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/
 */
export const ariaDisclosurePattern = (options: {
  collapsableContentId: string;
  expanded: boolean;
}): { buttonProps: React.ButtonHTMLAttributes<HTMLButtonElement> } => {
  return {
    buttonProps: {
      'aria-controls': options.collapsableContentId,
      'aria-expanded': options.expanded
    }
  };
};

/**
 * Groups aria attributes that belong to the menu pattern.
 *
 * https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/menu_role
 */
export const ariaMenuPattern = () => {
  return {
    toggleButtonProps: (options: { menuId: string; expanded: boolean }) => ({
      'aria-haspopup': true,
      'aria-controls': options.menuId,
      'aria-expanded': options.expanded
    }),
    menuProps: {
      role: 'menu'
    },
    menuItemProps: (options: {
      role: 'menuitem' | 'menuitemcheckbox' | 'menuitemradio';
      disabled: boolean | undefined;
    }) =>
      ({
        role: options.role,
        /**
         * As stated in the docs, a menuitem should always be focusable.
         * Disabled menuitems should be indicated with aria-disabled.
         */
        disabled: false,
        'aria-disabled': options.disabled,
        tabIndex: -1
      }) satisfies
        | React.ButtonHTMLAttributes<HTMLButtonElement>
        | React.AnchorHTMLAttributes<HTMLAnchorElement>,
    /**
     * Not explicitly required for the menu pattern, but the examples in the docs have this on the <li> elements.
     */
    listItemProps: {
      role: 'presentation'
    }
  };
};

/**
 * Returns attributes for a **single pair of header and panel** in an accordion.
 * Also make sure to use a heading (e.g. h3) tag around the button.
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/accordion/
 */
export const ariaAccordionPattern = (options: {
  buttonId: string;
  panelId: string;
  expanded: boolean;
}) => ({
  buttonProps: {
    ...ariaDisclosurePattern({ collapsableContentId: options.panelId, expanded: options.expanded })
      .buttonProps,
    id: options.buttonId
  },
  panelProps: {
    role: 'region',
    'aria-labelledby': options.buttonId
  }
});

/**
 * Returns attributes for elements in the aria dialog pattern.
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/dialog_role
 */
export const ariaDialogPattern = (options: {
  /**
   * Whether the dialog is modal, which means that navigation is limited to the dialog.
   * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes/aria-modal
   */
  modal: boolean;
  /**
   * From the docs:
   * > Use this attribute to label the dialog. Often, the value of the aria-labelledby attribute will be the id of the element used to title the dialog.
   */
  labelledBy?: string;
  /**
   * From the docs:
   * > Use this attribute to describe the contents of the dialog.
   */
  describedBy?: string;
}) => ({
  dialogProps: {
    role: 'dialog',
    'aria-modal': options.modal,
    'aria-labelledby': options.labelledBy,
    'aria-describedby': options.describedBy
  } satisfies React.HTMLAttributes<HTMLDivElement>
});
